import React, { useState, useMemo } from 'react';
import {
  ComposedChart, Line, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, ReferenceArea,
} from 'recharts';

const COLORS = {
  bg: '#0d1117',
  card: '#161b22',
  border: '#30363d',
  text: '#c9d1d9',
  textMuted: '#8b949e',
  green: '#3fb950',
  amber: '#d29922',
  red: '#f85149',
};

const LINE_CONFIG = [
  { key: 'composite_score', name: 'Composite', color: '#ffffff', width: 2.5 },
  { key: 'oil_avg', name: 'Oil/Energy', color: '#f97316', width: 1.5 },
  { key: 'fed_avg', name: 'Fed Policy', color: '#3b82f6', width: 1.5 },
  { key: 'geo_avg', name: 'Geopolitics', color: '#ef4444', width: 1.5 },
  { key: 'consumer_avg', name: 'Consumer', color: '#a855f7', width: 1.5 },
  { key: 'earnings_avg', name: 'Earnings', color: '#22c55e', width: 1.5 },
];

const signalDotColor = (signal) => {
  if (signal === 'BUY') return COLORS.green;
  if (signal === 'SELL') return COLORS.red;
  return COLORS.amber;
};

const formatTimestamp = (iso) => {
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const day = d.getDate();
  const h = d.getHours().toString().padStart(2, '0');
  const m = d.getMinutes().toString().padStart(2, '0');
  return `${month} ${day} ${h}:${m}`;
};

// Short label: just HH:MM, except show "MMM D" at midnight or first point
const formatTickShort = (value) => {
  if (!value) return '';
  // value is already "Mar 28 10:35" format
  const parts = value.split(' ');
  if (parts.length < 3) return value;
  const time = parts[2]; // "10:35"
  if (time === '00:00') return `${parts[0]} ${parts[1]}`; // day boundary → "Mar 28"
  return time; // just "10:35"
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div style={{
      background: COLORS.card,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 6,
      padding: '10px 14px',
      fontSize: 12,
      minWidth: 180,
    }}>
      <div style={{ color: COLORS.textMuted, marginBottom: 6 }}>{d.formattedTime}</div>
      <div style={{
        display: 'inline-block',
        background: signalDotColor(d.signal),
        color: '#000',
        fontWeight: 700,
        fontSize: 10,
        padding: '2px 8px',
        borderRadius: 4,
        marginBottom: 8,
      }}>
        {d.signal}
      </div>
      {LINE_CONFIG.map(l => (
        <div key={l.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 2 }}>
          <span style={{ color: l.color }}>{l.name}</span>
          <span style={{ fontFamily: 'monospace', color: COLORS.text }}>
            {d[l.key] != null ? d[l.key].toFixed(2) : '—'}
          </span>
        </div>
      ))}
      <div style={{ color: COLORS.textMuted, marginTop: 6, fontSize: 11 }}>
        {d.article_count} articles
      </div>
    </div>
  );
};

const SignalChangeDot = (props) => {
  const { cx, cy, payload } = props;
  if (!payload?._signalChanged || cx == null || cy == null) return null;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={6}
      fill={signalDotColor(payload.signal)}
      stroke={COLORS.card}
      strokeWidth={2}
    />
  );
};

export default function SignalTimeSeries({ flip_log, refreshInterval = 900000 }) {
  const [hiddenLines, setHiddenLines] = useState({});

  const chartData = useMemo(() => {
    if (!flip_log?.length) return [];
    return flip_log.map((point, i) => ({
      ...point,
      formattedTime: formatTimestamp(point.timestamp),
      _signalChanged: i > 0 && point.signal !== flip_log[i - 1].signal,
    }));
  }, [flip_log]);

  const timeRange = useMemo(() => {
    if (chartData.length < 2) return '';
    const first = new Date(chartData[0].timestamp);
    const last = new Date(chartData[chartData.length - 1].timestamp);
    const month = last.toLocaleString('en-US', { month: 'short' });
    const day = last.getDate();
    return `Last ${chartData.length} data points \u00b7 ${month} ${day}`;
  }, [chartData]);

  const tickInterval = useMemo(() => {
    if (chartData.length <= 6) return 0;
    return Math.ceil(chartData.length / 6) - 1; // max ~6 ticks
  }, [chartData]);

  const toggleLine = (dataKey) => {
    setHiddenLines(prev => ({ ...prev, [dataKey]: !prev[dataKey] }));
  };

  if (!flip_log || flip_log.length < 2) {
    return (
      <div style={{
        background: COLORS.card,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8,
        padding: '24px',
        marginBottom: 40,
      }}>
        <div style={{ color: COLORS.textMuted, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>
          Signal History
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 20px',
          color: COLORS.textMuted,
          fontSize: 14,
          textAlign: 'center',
          gap: 12,
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={COLORS.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          Chart will populate as data accumulates — next update in ~15 minutes
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: COLORS.card,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 8,
      padding: '24px 24px 16px',
      marginBottom: 40,
    }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ color: COLORS.textMuted, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
          Signal History
        </div>
        {timeRange && (
          <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 4 }}>
            {timeRange}
          </div>
        )}
      </div>

      <ResponsiveContainer width="100%" height={380}>
        <ComposedChart data={chartData} margin={{ top: 10, right: 40, bottom: 50, left: 10 }}>
          {/* Background zones */}
          <ReferenceArea y1={0.60} y2={1} fill="#3fb950" fillOpacity={0.06} />
          <ReferenceArea y1={-1} y2={-0.25} fill="#f85149" fillOpacity={0.06} />

          <XAxis
            dataKey="formattedTime"
            tickFormatter={formatTickShort}
            tick={{ fill: COLORS.textMuted, fontSize: 10, angle: -45, textAnchor: 'end', dy: 4 }}
            axisLine={{ stroke: COLORS.border }}
            tickLine={false}
            interval={tickInterval}
            height={55}
          />
          <YAxis
            domain={[-1, 1]}
            ticks={[-1, -0.5, 0, 0.5, 1]}
            tick={{ fill: COLORS.textMuted, fontSize: 10 }}
            axisLine={{ stroke: COLORS.border }}
            tickLine={false}
          />

          {/* Grid-like reference at 0 */}
          <ReferenceLine y={0} stroke={COLORS.border} strokeDasharray="3 3" />

          {/* Threshold lines */}
          <ReferenceLine
            y={0.60}
            stroke={COLORS.green}
            strokeDasharray="6 4"
            label={{ value: 'BUY', fill: COLORS.green, fontSize: 10, position: 'insideTopRight', offset: 4 }}
          />
          <ReferenceLine
            y={-0.25}
            stroke={COLORS.red}
            strokeDasharray="6 4"
            label={{ value: 'SELL', fill: COLORS.red, fontSize: 10, position: 'insideBottomRight', offset: 4 }}
          />

          <Tooltip content={<CustomTooltip />} />

          <Legend
            onClick={(e) => toggleLine(e.dataKey)}
            wrapperStyle={{ fontSize: 11, cursor: 'pointer', paddingTop: 8 }}
            formatter={(value, entry) => (
              <span style={{
                color: hiddenLines[entry.dataKey] ? COLORS.border : entry.color,
                textDecoration: hiddenLines[entry.dataKey] ? 'line-through' : 'none',
              }}>
                {value}
              </span>
            )}
          />

          {LINE_CONFIG.map(l => (
            <Line
              key={l.key}
              type="monotone"
              dataKey={l.key}
              name={l.name}
              stroke={l.color}
              strokeWidth={l.width}
              dot={l.key === 'composite_score' ? <SignalChangeDot /> : false}
              activeDot={{ r: 4, fill: l.color }}
              hide={!!hiddenLines[l.key]}
              connectNulls
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
